// External deps
import { useRef, useState } from 'react';
import { PropTypes } from 'prop-types';
import { ModalGeneric, Loader } from 'ypf-pas-general-ui-components';
import { IoMdClose } from 'react-icons/io';

// Internal deps
import MonthlyImputationObject from '../MonthlyImputationObject/MonthlyImputationObject';
import useToast from '../../../../hooks/useToast';
import { statusEnum, ubicationTypeEnum } from '../../../../utils/dataStatus';
import { putUbicationMonthlyDetail } from '../../../../adapters/requestServiceDetails/requestServiceDetailsAdapter';
import { updateimputationObjectMSDSApprover } from '../../../../services/workReport';

import styles from './ModalMonthlyImputationObject.module.scss';

const ModalMonthlyImputationObject = ({
  canAddWellToImputationObject,
  canEditImputationObject,
  data,
  onClose,
  show,
  disabledEditStateCancelOrFinish,
  isOyM,
  idSolicitud
}) => {
  const [loading, setLoading] = useState(false);
  const [completeImputationObject, setCompleteImputationObject] = useState(false);
  const [disabledActions, setDisabledActions] = useState(false);

  const form = useRef(null);
  const { setNewToast } = useToast();

  const finalStatus =
    data?.statusWorkReportEnum === statusEnum.cancelada ||
    data?.statusWorkReportEnum === statusEnum.finalizada;

  const updateWellsWithModifiedProps2 = (originalWells, copyWells) =>
    originalWells
      ?.filter((originalWell, index) => {
        if (!copyWells?.length) return false;
        const modifiedWell = copyWells[index];

        const isImputationObjectModified =
          originalWell?.imputationObject?.id !== modifiedWell?.imputationObject?.id;
        const isImputationTypeModified =
          originalWell?.imputationType?.id !== modifiedWell?.imputationType?.id;

        return isImputationObjectModified || isImputationTypeModified;
      })
      .map((originalWell, index) => {
        const modifiedWell = copyWells[index];
        const updatedWell = { ...originalWell };

        // Update relevant values that have changed
        if (originalWell?.socCode !== modifiedWell?.socCode?.nombre) {
          updatedWell.socCode = modifiedWell?.socCode?.nombre || originalWell.socCode;
        }
        if (originalWell?.logisticCenter !== modifiedWell?.logisticCenter?.nombre) {
          updatedWell.logisticCenter =
            modifiedWell?.logisticCenter?.nombre || originalWell.logisticCenter;
        }

        return updatedWell;
      });

  const createBodyPostArray = (wellsArray, pozoIdManagment) =>
    wellsArray.map((well) => ({
      ...(pozoIdManagment && {
        pozoId: well?.well?.id
      }),
      sociedad: well?.socCode,
      centro: well?.logisticCenter?.length > 1 ? String(well?.logisticCenter) : null,
      tipoImputacion: well?.imputationType?.value,
      objetoImputacion: {
        id: well.imputationObject?.id,
        nombre: well.imputationObject?.nombre.split(' ')[0]
      }
    }));

  const handleUpdateImputationObject = async () => {
    const originalWells = form?.current?.values?.imputationObject?.wells;
    const copyWells = form?.current?.values?.imputationObject?.copyWells;
    const updatedWellsArray = updateWellsWithModifiedProps2(originalWells, copyWells);
    const imputationObjectValues = form.current?.values?.imputationObject;

    if (finalStatus) {
      onClose();
    } else {
      setLoading(true);
      setCompleteImputationObject(false);
      if (imputationObjectValues?.esSoloAprobador) {
        const isLocationOrWell =
          data?.tipoUbicacionEnum === ubicationTypeEnum.pozo ||
          data?.tipoUbicacionEnum === ubicationTypeEnum.locacion;

        const bodyPostWellsArray = createBodyPostArray(updatedWellsArray, isLocationOrWell);

        updateimputationObjectMSDSApprover(
          { idSolicitud, idUbicacion: data?.id },
          bodyPostWellsArray
        )
          .then(() => {
            setLoading(false);
            onClose();
            setNewToast({
              type: 'SUCCESS',
              message: 'Los cambios fueron guardados correctamente'
            });
          })
          .catch((err) => {
            setLoading(false);
            setNewToast({
              type: 'ERROR',
              message: err.message
            });
          });
      } else {
        putUbicationMonthlyDetail({
          wells: originalWells,
          requestId: data?.id,
          isOyM
        })
          .then(() => {
            setLoading(false);
            onClose();
            setNewToast({
              type: 'SUCCESS',
              message: 'Los cambios fueron guardados correctamente'
            });
          })
          .catch((err) => {
            setLoading(false);
            setNewToast({
              type: 'ERROR',
              message: err.message
            });
          });
      }
    }
  };

  return (
    <ModalGeneric
      backdrop="static"
      infoModal
      onHide={onClose}
      primaryAction={handleUpdateImputationObject}
      primaryActionDisabled={disabledEditStateCancelOrFinish || disabledActions}
      primaryActionLabel="Aceptar"
      secondaryAction={onClose}
      secondaryActionLabel="Salir"
      show={show}
      size="xl"
      title="Detalles del tipo y objeto de imputación"
    >
      <>
        {loading && <Loader show text="CARGANDO ..." />}
        {data ? (
          <MonthlyImputationObject
            canAddWellToImputationObject={
              canAddWellToImputationObject && !disabledEditStateCancelOrFinish
            }
            canEditImputationObject={canEditImputationObject && !disabledEditStateCancelOrFinish}
            data={data}
            form={form}
            isOyM={isOyM}
            readOnly={finalStatus}
            setDisabledActions={setDisabledActions}
            setLoading={setLoading}
          />
        ) : null}

        {completeImputationObject ? (
          <div className="d-flex justify-content-start align-items-center mt-2">
            <span className={`mt-2 ${styles['incomplete-imputation-object-error']}`}>
              Debe completar el tipo y objeto de imputación
              <IoMdClose
                onClick={() => setCompleteImputationObject(false)}
                style={{ cursor: 'pointer', fontWeight: 'bold' }}
              />
            </span>
          </div>
        ) : null}
      </>
    </ModalGeneric>
  );
};

ModalMonthlyImputationObject.propTypes = {
  canAddWellToImputationObject: PropTypes.bool.isRequired,
  canEditImputationObject: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.shape),
  onClose: PropTypes.func,
  show: PropTypes.bool,
  disabledEditStateCancelOrFinish: PropTypes.bool.isRequired,
  isOyM: PropTypes.bool.isRequired,
  idSolicitud: PropTypes.number.isRequired
};
ModalMonthlyImputationObject.defaultProps = {
  data: null,
  show: false,
  onClose: () => {}
};
export default ModalMonthlyImputationObject;
